import { Component, Prop, h, Host } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Company logos section for SignNow brand showcasing trusted client logos.
 * Wraps `company-logos-type-1` with support for carousel, column, and default layouts.
 * The heading is inline-editable via slot; logos are managed via the `logos` prop.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @slot heading - <me-heading variant="6" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading text
 *
 * @example
 *   <sn-company-logos-type-1
 *     logos='[{"src":"https://marketing-static.signnow.com/323/images/home-new/logos/walmart.svg","alt":"Walmart"},{"src":"https://marketing-static.signnow.com/323/images/home-new/logos/apple.svg","alt":"Apple"},{"src":"https://marketing-static.signnow.com/323/images/home-new/logos/comcast.svg","alt":"Comcast"},{"src":"https://marketing-static.signnow.com/323/images/home-new/logos/facebook.svg","alt":"Facebook"}]'
 *   >
 *     <me-heading slot="heading" variant="6" tag="h2">Forward-thinking companies around the world trust airSlate SignNow</me-heading>
 *   </sn-company-logos-type-1>
 */
@Component({
  tag: 'sn-company-logos-type-1',
  shadow: false,
})
export class SNCompanyLogosType1 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Display variant: default, column, or carousel
   * @default carousel
   */
  @Prop() variant: 'default' | 'column' | 'carousel' = 'carousel';

  /**
   * Enable/disable carousel autoplay
   * @default true
   */
  @Prop() autoplay: boolean = true;

  /**
   * List of partner or client logos to display.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {image} src - Logo image source URL (SVG or PNG recommended)
   * @item.property {string} alt - Accessible alt text describing the company or brand
   * @item.required src
   * @item.required alt
   * @item.content alt
   * @minItems 4
   */
  @Prop({ mutable: true }) logos!: any[] | string;

  componentWillLoad() {
    this.logos = parseJsonProp(this.logos, []);
  }

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <company-logos-type-1 logos={Array.isArray(this.logos) ? this.logos : []} variant={this.variant} autoplay={this.autoplay}>
          <slot name="heading"></slot>
        </company-logos-type-1>
      </Host>
    );
  }
}

